import { useCallback, useEffect, useState } from 'react';
import { logout } from '../auth/session';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { FixtureBanner } from '../components/FixtureBanner';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { AppPage } from '../pages/AppPage';
import { KnowledgePage } from '../pages/KnowledgePage';
import { RunPage } from '../pages/RunPage';
import { WorkspacePage } from '../pages/WorkspacePage';
import { WorkspacesPage } from '../pages/WorkspacesPage';
import { StudioPage } from '../studio/StudioPage';
import { Link } from './Link';
import { useRoute } from './navigation';
import { usePlatform } from './platform';
import { useAsync } from './useAsync';
import { href, type KnowledgeRoute, type Route, type WorkspaceRoute, type WorkspacesRoute } from './router';

/**
 * 画面はWorkspace階層（一覧・詳細）とmicroapp階層（app詳細・実行詳細）の2つのシェルに分かれる。
 * どちらもナビゲーションだけが暗色で、管理ページ本体は明色。
 *
 * authEnabledはCognitoでログイン済みのときだけtrue。認証が無効なlocalではログアウトを出さない。
 */
export function App({ authEnabled = false }: { authEnabled?: boolean }) {
  const { route, navigate } = useRoute();
  const { mode } = usePlatform();
  const handleLogout = useCallback(() => void logout(), []);
  // サイドバーのWorkspace一覧を取り直す合図。サイドバーはWorkspace階層のページ間で
  // 生き続けるので遷移では取り直されず、Workspaceの削除はSSEのeventも出さない。
  // そのため、消した画面から数を進めて明示的に取り直させる。
  const [workspaceListVersion, setWorkspaceListVersion] = useState(0);
  const reloadWorkspaceList = useCallback(
    () => setWorkspaceListVersion((version) => version + 1),
    [],
  );

  return (
    <>
      <FixtureBanner mode={mode} />
      {renderRoute(
        route,
        navigate,
        { version: workspaceListVersion, reload: reloadWorkspaceList },
        authEnabled ? handleLogout : undefined,
      )}
    </>
  );
}

/** サイドバーのWorkspace一覧を取り直す合図。versionが変わるとWorkspaceNavが取り直す。 */
interface WorkspaceListSignal {
  version: number;
  reload(): void;
}

function renderRoute(
  route: Route,
  navigate: ReturnType<typeof useRoute>['navigate'],
  workspaceList: WorkspaceListSignal,
  onLogout: (() => void) | undefined,
) {
  switch (route.name) {
    case 'workspaces':
    case 'workspace':
    case 'knowledge':
      return (
        <WorkspaceShell
          brandHref={href({ name: 'workspaces' })}
          sidebar={
            <WorkspaceNav route={route} navigate={navigate} listVersion={workspaceList.version} />
          }
          onLogout={onLogout}
        >
          {route.name === 'workspaces' ? (
            <WorkspacesPage navigate={navigate} />
          ) : route.name === 'knowledge' ? (
            <KnowledgePage route={route} navigate={navigate} />
          ) : (
            <WorkspacePage
              route={route}
              navigate={navigate}
              onWorkspaceDeleted={workspaceList.reload}
            />
          )}
        </WorkspaceShell>
      );
    case 'studio':
      // Studio は 3 ペインの専用シェル（StudioShell）を StudioPage が自分で描く。
      return <StudioPage route={route} navigate={navigate} onLogout={onLogout} />;
    case 'app':
      return (
        <AppShell brandHref={href({ name: 'workspaces' })} onLogout={onLogout}>
          <AppPage route={route} navigate={navigate} />
        </AppShell>
      );
    case 'run':
      return (
        <AppShell brandHref={href({ name: 'workspaces' })} onLogout={onLogout}>
          <RunPage route={route} navigate={navigate} />
        </AppShell>
      );
    case 'notFound':
      return (
        <WorkspaceShell brandHref={href({ name: 'workspaces' })} onLogout={onLogout}>
          <EmptyState
            title="このURLに対応する画面がありません"
            description={route.path}
            action={
              <Link to={{ name: 'workspaces' }} onNavigate={navigate}>
                Workspace一覧へ
              </Link>
            }
          />
        </WorkspaceShell>
      );
  }
}

/**
 * サイドバーのWorkspace一覧。Workspace階層のどのページでも同じ並びを出し、
 * 詳細を見ながら別Workspaceへ移れるようにする。
 * 絞り込み条件の正本はURL（一覧ルートのq）に置き、検索するとその条件の一覧ページへ移る。
 */
function WorkspaceNav({
  route,
  navigate,
  listVersion,
}: {
  route: WorkspacesRoute | WorkspaceRoute | KnowledgeRoute;
  navigate: ReturnType<typeof useRoute>['navigate'];
  /** 変わったら一覧を取り直す。Workspaceを削除した画面が進める。 */
  listVersion: number;
}) {
  const { api } = usePlatform();
  const query = route.name === 'workspaces' ? (route.q ?? '') : '';
  const [search, setSearch] = useState(query);
  const list = useAsync(
    () => api.listWorkspaces(query ? { q: query } : {}),
    [api, query, listVersion],
  );

  // このナビはWorkspace詳細へ移っても残り続けるので、入力欄をURLの条件へ追従させる。
  useEffect(() => setSearch(query), [query]);

  return (
    <nav className="workspace-nav" aria-label="Workspace">
      <form
        className="workspace-nav-search"
        onSubmit={(event) => {
          event.preventDefault();
          navigate(search ? { name: 'workspaces', q: search } : { name: 'workspaces' });
        }}
      >
        <input
          className="input workspace-nav-input"
          type="search"
          value={search}
          placeholder="Workspaceを検索"
          aria-label="Workspaceを検索"
          onChange={(event) => setSearch(event.target.value)}
        />
      </form>

      {list.loading && (
        <p className="workspace-nav-status">
          <Spinner label="Workspaceを取得しています" />
        </p>
      )}
      <ProblemNotice
        problem={list.problem}
        action={
          <Button variant="text" onClick={list.reload}>
            再取得
          </Button>
        }
      />

      {list.data && list.data.items.length === 0 && (
        <p className="workspace-nav-status">
          {query ? '条件に合うWorkspaceがありません' : 'Workspaceがまだありません'}
        </p>
      )}

      {list.data && list.data.items.length > 0 && (
        <ul className="workspace-nav-list">
          {list.data.items.map((workspace) => (
            <li key={workspace.id}>
              <Link
                className="nav-item"
                to={{ name: 'workspace', workspaceId: workspace.id }}
                current={
                  (route.name === 'workspace' || route.name === 'knowledge') &&
                  route.workspaceId === workspace.id
                }
                onNavigate={navigate}
              >
                <span className="workspace-nav-initial" aria-hidden="true">
                  {workspace.name.slice(0, 1)}
                </span>
                <span className="workspace-nav-name">{workspace.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {list.data?.nextCursor && (
        <p className="workspace-nav-status">
          先頭ページのみ表示しています。続きは検索で絞り込んでください。
        </p>
      )}

      <Link
        className="nav-item workspace-nav-create"
        to={{ name: 'workspaces' }}
        current={route.name === 'workspaces'}
        onNavigate={navigate}
      >
        <span className="workspace-nav-plus" aria-hidden="true">
          +
        </span>
        <span className="workspace-nav-name">Workspaceを作る</span>
      </Link>
    </nav>
  );
}
